import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CommonContext } from '../../component/common/context/commonContext'
import { getContentSize } from '../../utils/screenUtils'
import { useMountEffect } from 'primereact/hooks'
import PdaTitle from '../../component/common/PdaTitle'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

// 타입 정의
interface GoodsItem {
    goodsId: string
    goodsName: string
    orderQty: number
    pickedQty: number
    unitCode: string
    goodsTmprCd: string
    isPicked: boolean
    outbId: number
    location: string
}

interface LocationPickingItem {
    location: string
    totalOrderQty: number
    totalPickedQty: number
    completionRate: string
    goodsList: GoodsItem[]
}

interface TotalPickingResponse {
    totalPickingCode: string
    searchConditions: string
    locationPickingList: LocationPickingItem[]
}

const TotalPicking = () => {
    const { $http, $toast } = useContext(CommonContext)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const scanRef = useRef<HTMLInputElement>(null)
    const [totalPickingCode, setTotalPickingCode] = useState<string>('')
    const [height, setHieght] = useState<number>(getContentSize())
    const [params, setParams] = useState<{ totalPickingCode: string }>({
        totalPickingCode: '',
    })
    const [totalPickingData, setTotalPickingData] = useState<TotalPickingResponse | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [productBarcodes, setProductBarcodes] = useState<{[location: string]: string}>({})
    const [showQuantityDialog, setShowQuantityDialog] = useState<boolean>(false)
    const [selectedGoods, setSelectedGoods] = useState<GoodsItem | null>(null)
    const [pickingQuantity, setPickingQuantity] = useState<number>(1)
    const quantityInputRef = useRef<HTMLInputElement>(null)
    const [showIncompleteDialog, setShowIncompleteDialog] = useState<boolean>(false)
    const [incompleteItems, setIncompleteItems] = useState<string>('')

    useMountEffect(() => {
        setTimeout(() => {
            if (scanRef && scanRef.current) {
                scanRef.current.focus()
                scanRef.current.select()
                setHieght(getContentSize())
            }
        }, 300)
    })

    const findTotalPicking = useCallback(
        async (params: { totalPickingCode: string }) => {
            try {
                setLoading(true)
                const response = await $http().get('/outbound/total-picking', {
                    params: { totalPickingCode: params.totalPickingCode },
                })
                
                    setTotalPickingData(response)
            } catch (error: any) {
                console.error('토탈 피킹 조회 실패:', error)
                $toast({ 
                    title: t(tokens.common.fail), 
                    text: error.response?.data?.message || '토탈 피킹 정보 조회에 실패했습니다.', 
                    icon: 'error' 
                })
            } finally {
                setLoading(false)
            }
        },
        [$http, $toast, navigate],
    )

    useEffect(() => {
        if (params.totalPickingCode) {
            findTotalPicking(params)
        }
    }, [params, findTotalPicking])

    const onKeyDownSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            setParams({
                ...params,
                totalPickingCode,
            })
        }
    }

    const onChangeTotalPickingCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        setParams({
            ...params,
            totalPickingCode: '',
        })
        setTotalPickingCode(e.currentTarget.value)
    }

    const onStartLocationPicking = (locationItem: LocationPickingItem) => {
        // 로케이션별 피킹 시작
        navigate('/outbound/piece-picking', { 
            state: { 
                totalPickingCode: totalPickingData?.totalPickingCode,
                location: locationItem.location,
                goodsList: locationItem.goodsList
            } 
        })
    }

    const getLocationProgress = (locationItem: LocationPickingItem) => {
        return `${locationItem.totalPickedQty}/${locationItem.totalOrderQty} (${locationItem.completionRate})`
    }

    const handleProductBarcodeInput = (barcode: string, locationItem: LocationPickingItem) => {
        console.log('바코드 입력 처리:', barcode, '로케이션:', locationItem.location)
        
        // 바코드로 상품 찾기
        const foundGoods = locationItem.goodsList.find(goods => 
            goods.goodsId === barcode || goods.goodsName.includes(barcode)
        )

        console.log('찾은 상품:', foundGoods)

        if (foundGoods) {
            console.log('상품 정보:', {
                goodsName: foundGoods.goodsName,
                orderQty: foundGoods.orderQty,
                pickedQty: foundGoods.pickedQty
            })
            
            setSelectedGoods(foundGoods)
            // 모든 상품에 대해 수량 입력 다이얼로그 표시
            console.log('수량 입력 다이얼로그 표시')
            setPickingQuantity(1)
            setShowQuantityDialog(true)
            // 다이얼로그가 열린 후 입력창 초기화
            setTimeout(() => {
                if (quantityInputRef.current) {
                    quantityInputRef.current.value = ''
                    quantityInputRef.current.focus()
                }
            }, 100)
        } else {
            $toast({ 
                title: t(tokens.common.fail), 
                text: '해당 상품을 찾을 수 없습니다.', 
                icon: 'error' 
            })
        }
    }

    const handlePickingConfirm = async (quantity: number) => {
        if (selectedGoods && totalPickingData) {
            try {
                setLoading(true)
                const response = await $http().post('/outbound/total-picking/scan', {
                    totalPickingCode: totalPickingData.totalPickingCode,
                    locationPath: selectedGoods.location,
                    goodsId: selectedGoods.goodsId,
                    scannedQty: quantity
                })
                
                // 응답 데이터로 상태 업데이트
                updateGoodsPickedQty(selectedGoods.goodsId, selectedGoods.location, response.pickedQty)
                
                $toast({ 
                    title: t(tokens.common.success), 
                    text: `${selectedGoods.goodsName} ${quantity}개 피킹 처리되었습니다. (${response.pickedQty}/${response.orderQty})`, 
                    icon: 'success' 
                })
                
                // 해당 로케이션의 바코드 초기화
                setProductBarcodes(prev => ({
                    ...prev,
                    [selectedGoods.location]: ''
                }))
            } catch (error: any) {
                console.error('피킹 처리 실패:', error)
                $toast({
                    title: t(tokens.common.fail),
                    text: error.response?.data?.message || '피킹 처리에 실패했습니다.',
                    icon: 'error'
                })
            } finally {
                setLoading(false)
            }
        }
        setShowQuantityDialog(false)
        setSelectedGoods(null)
    }

    const updateGoodsPickedQty = (goodsId: string, location: string, newPickedQty: number) => {
        if (totalPickingData) {
            const updatedData = { ...totalPickingData }
            const locationIndex = updatedData.locationPickingList.findIndex(loc => loc.location === location)
            
            if (locationIndex !== -1) {
                const goodsIndex = updatedData.locationPickingList[locationIndex].goodsList.findIndex(
                    goods => goods.goodsId === goodsId
                )
                
                if (goodsIndex !== -1) {
                    updatedData.locationPickingList[locationIndex].goodsList[goodsIndex].pickedQty = newPickedQty
                    updatedData.locationPickingList[locationIndex].goodsList[goodsIndex].isPicked = 
                        newPickedQty >= updatedData.locationPickingList[locationIndex].goodsList[goodsIndex].orderQty
                    
                    // 로케이션별 총 피킹 수량 업데이트
                    const totalPicked = updatedData.locationPickingList[locationIndex].goodsList.reduce(
                        (sum, goods) => sum + goods.pickedQty, 0
                    )
                    updatedData.locationPickingList[locationIndex].totalPickedQty = totalPicked
                    updatedData.locationPickingList[locationIndex].completionRate = 
                        `${((totalPicked / updatedData.locationPickingList[locationIndex].totalOrderQty) * 100).toFixed(1)}%`
                    
                    setTotalPickingData(updatedData)
                }
            }
        }
    }

    const handleCompletePicking = async () => {
        if (totalPickingData) {
            try {
                setLoading(true)
                const response = await $http().post('/outbound/total-picking/complete', {
                    totalPickingCode: totalPickingData.totalPickingCode
                })
                
                if (response.isCompleted) {
                    $toast({ 
                        title: t(tokens.common.success), 
                        text: `토탈 피킹이 완료되었습니다. (${response.completedItems}/${response.totalItems})`, 
                        icon: 'success' 
                    })
                    
                    // 완료 후 토탈 피킹 조회 화면으로 초기화
                    setTotalPickingData(null)
                    setTotalPickingCode('')
                    setProductBarcodes({})
                    setParams({ totalPickingCode: '' })
                    
                    // 토탈 피킹 바코드 입력창에 포커스
                    setTimeout(() => {
                        if (scanRef && scanRef.current) {
                            scanRef.current.focus()
                        }
                    }, 100)
                } else {
                    // 미완료 항목들 표시
                    const remainingItems = response.remainingItems.map((item: any) => 
                        `${item.locationPath} - ${item.goodsId} (${item.remainingQty}개 남음)`
                    ).join('\n\n')
                    
                    setIncompleteItems(remainingItems)
                    setShowIncompleteDialog(true)
                }
            } catch (error: any) {
                console.error('피킹 완료 처리 실패:', error)
                $toast({
                    title: t(tokens.common.fail),
                    text: error.response?.data?.message || '피킹 완료 처리에 실패했습니다.',
                    icon: 'error'
                })
            } finally {
                setLoading(false)
            }
        }
    }

    return (
        <>
            <PdaTitle title="토탈 피킹" historyBack goToHome />
            
            {/* 바코드 스캔 입력 */}
            <div className="grid pda-search p-3">
                <div className="col-12 flex">
                    <InputText
                        ref={scanRef}
                        value={totalPickingCode}
                        className="p-inputtext-md w-full"
                        placeholder={t(tokens.common.please_scan_total_picking_barcode)}
                        onChange={(e) => onChangeTotalPickingCode(e)}
                        onKeyDown={(e) => onKeyDownSearch(e)}
                        inputMode="none"
                        autoFocus={true}
                        disabled={loading}
                    />
                </div>
            </div>

            {/* 토탈 피킹 정보가 있을 때만 표시 */}
            {totalPickingData ? (
                <div className="grid p-3">
                        <div className="col-12">
                            <div className="card">
                                <div className="p-3">
                                    <div className="text-center mb-3">
                                        <strong>로케이션 수: {totalPickingData.locationPickingList.length}개</strong>
                                    </div>
                                    <div className="text-center">
                                        <Button
                                            label="피킹완료"
                                            icon="pi pi-check"
                                            className="p-button-success w-full"
                                            onClick={handleCompletePicking}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                    {/* 로케이션별 피킹 목록 - 필름 형태 */}
                    <div className="col-12">
                        <div className="card">
                            <div className="p-3">
                                
                                {/* 필름 형태 스크롤 컨테이너 */}
                                <div 
                                    className="flex gap-3 overflow-x-auto pb-3" 
                                    style={{ 
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#ccc transparent'
                                    }}
                                >
                                    {totalPickingData.locationPickingList.map((locationItem, index) => (
                                        <div 
                                            key={index} 
                                            className="flex-shrink-0 border-round shadow-2" 
                                            style={{ 
                                                minWidth: '300px',
                                                maxWidth: '350px',
                                                border: '1px solid #ddd',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            {/* 로케이션 헤더 */}
                                            <div className="p-3 border-bottom-1 surface-border">
                                                <div className="text-center mb-3">
                                                    <h4 className="m-0 text-primary font-bold">{locationItem.location}</h4>
                                                </div>
                                                <InputText
                                                    placeholder="상품 바코드를 스캔하세요"
                                                    className="w-full p-inputtext-sm"
                                                    value={productBarcodes[locationItem.location] || ''}
                                                    onChange={(e) => {
                                                        setProductBarcodes(prev => ({
                                                            ...prev,
                                                            [locationItem.location]: e.target.value
                                                        }))
                                                    }}
                                                    onKeyDown={(e) => {
                                                        const currentBarcode = productBarcodes[locationItem.location] || ''
                                                        if (e.key === 'Enter' && currentBarcode.trim()) {
                                                            handleProductBarcodeInput(currentBarcode.trim(), locationItem)
                                                        }
                                                    }}
                                                />
                                            </div>
                                            
                                            {/* 상품 목록 - 세로 스크롤 */}
                                            <div 
                                                className="p-3" 
                                                style={{ 
                                                    maxHeight: '400px',
                                                    overflowY: 'auto',
                                                    scrollbarWidth: 'thin'
                                                }}
                                            >
                                                <div className="grid">
                                                    {locationItem.goodsList.map((goods, goodsIndex) => (
                                                        <div key={goodsIndex} className="col-12 mb-2">
                                                            <div 
                                                                className={`p-2 border-round text-sm border-1 ${
                                                                    goods.isPicked 
                                                                        ? 'bg-green-50 border-green-200' 
                                                                        : 'surface-border'
                                                                }`}
                                                            >
                                                                <div className={`text-sm font-bold ${
                                                                    goods.isPicked ? 'text-green-700' : ''
                                                                }`}>
                                                                    {goods.goodsName}
                                                                </div>
                                                                <div className={`text-xs mt-1 ${
                                                                    goods.isPicked ? 'text-green-600' : 'text-600'
                                                                }`}>
                                                                    {goods.goodsId}
                                                                </div>
                                                                <div className={`text-xs mt-1 ${
                                                                    goods.isPicked ? 'text-green-600' : 'text-primary'
                                                                }`}>
                                                                    ({goods.pickedQty}/{goods.orderQty})
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* 바코드 스캔 안내 메시지 */
                <div className="grid pl-2">
                    <div className="font-bold text-center w-full vertical-align-middle" style={{ paddingTop: height / 3 }}>
                        {t(tokens.common.please_scan_total_picking_barcode)}
                    </div>
                </div>
                )}

                {/* 수량 입력 다이얼로그 */}
                <Dialog
                    header="피킹 수량 입력"
                    visible={showQuantityDialog}
                    style={{ width: '90vw', maxWidth: '400px' }}
                    onHide={() => setShowQuantityDialog(false)}
                >
                    {selectedGoods && (
                        <div className="p-3">
                            <div className="mb-3">
                                <div className="font-bold">{selectedGoods.goodsName}</div>
                                <div className="text-sm text-600">{selectedGoods.goodsId}</div>
                                <div className="text-sm text-primary">
                                    피킹 가능 수량: {selectedGoods.orderQty - selectedGoods.pickedQty}개
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="block mb-2">피킹할 수량을 입력하세요:</label>
                                <InputText
                                    ref={quantityInputRef}
                                    type="number"
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 1
                                        console.log('수량 변경:', e.target.value, '->', value)
                                        setPickingQuantity(value)
                                    }}
                                    min={1}
                                    max={selectedGoods.orderQty - selectedGoods.pickedQty}
                                    className="w-full"
                                    placeholder="수량을 입력하세요"
                                />
                            </div>
                            
                            <div className="flex gap-2 justify-content-end">
                                <Button
                                    label="취소"
                                    icon="pi pi-times"
                                    className="p-button-text"
                                    onClick={() => setShowQuantityDialog(false)}
                                />
                                <Button
                                    label="확인"
                                    icon="pi pi-check"
                                    onClick={() => handlePickingConfirm(pickingQuantity)}
                                />
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* 미완료 항목 Dialog */}
                <Dialog
                    header="피킹 미완료"
                    visible={showIncompleteDialog}
                    style={{ width: '90vw', maxWidth: '500px' }}
                    onHide={() => setShowIncompleteDialog(false)}
                >
                    <div className="p-3">
                        <div className="mb-3">
                            <div className="text-center mb-3">
                                <i className="pi pi-exclamation-triangle text-orange-500 text-4xl"></i>
                            </div>
                            <div className="text-center font-bold text-lg mb-3">
                                아직 완료되지 않은 항목이 있습니다.
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <div className="font-bold mb-2">미완료 항목:</div>
                            <div 
                                className="p-3 border-round bg-orange-50 border-1 border-orange-200"
                                style={{ 
                                    whiteSpace: 'pre-line',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}
                            >
                                {incompleteItems}
                            </div>
                        </div>
                        
                        <div className="text-center">
                            <Button
                                label="확인"
                                icon="pi pi-check"
                                className="p-button-primary"
                                onClick={() => setShowIncompleteDialog(false)}
                            />
                        </div>
                    </div>
                </Dialog>
            </>
        )
    }

export default TotalPicking
